import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { gameState } from '../state/gameState.js';
import { showModal } from './Modal.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { resultHeadline } from './quizLogic.mjs';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { ensureLoaded } from './sceneLoading.js';

// Same idiom as Modal.js: world-space centring on the camera view so hit-testing stays
// correct in scrolling scenes.
function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

const W = 900, H = 640, IMG_W = 520, IMG_H = 320;

// The pictures are big (~1-2 MB decoded each) and a phone does not prefetch them: load this
// quiz's before its first question (a short bar; nothing to wait for when they are in).
const loadPictures = (scene, questions) => ensureLoaded(scene, questions.map((q) => q.image).filter(Boolean));

// One question per screen, no going back, no timer. Answers post immediately; the
// result arrives with the last answer. ✕ = abandon (exam: confirm first — it counts as a fail).
export async function showQuizModal(scene, { kind, quiz }) {
  await loadPictures(scene, quiz.questions);
  return new Promise((resolve) => {
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);
    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff).setStrokeStyle(4, 0x333333);
    layer.add([overlay, box]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });
    const closeBtn = iconText(scene, x + W / 2 - 14, y - H / 2 + 8, '×', { fontFamily: 'system-ui', fontSize: '26px', color: '#a33', fontStyle: 'bold' })
      .setOrigin(1, 0).setInteractive({ useHandCursor: true });
    layer.add(closeBtn);

    // A resumed session (answered ids already posted) skips ahead; a fresh one keeps all.
    const questions = quiz.questions.filter((q) => !quiz.answered?.includes(q.id));
    const done = quiz.questions.length - questions.length;
    let idx = 0, busy = false;
    let items = [];

    const clear = () => { items.forEach((o) => o.destroy()); items = []; };
    // `closed` guards against a second finish() — the ✕ handler awaits an abort POST and
    // an answer can resolve in the meantime; a double finish would unlockInput twice.
    let closed = false;
    const finish = (result) => {
      if (closed) return;
      closed = true;
      audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(result);
    };

    const render = () => {
      clear();
      const q = questions[idx];
      const header = `${kind === 'exam' ? 'Egzamin' : 'Test próbny'} · pytanie ${done + idx + 1} / ${quiz.questions.length}`;
      const r = renderQuestion({ scene, layer, x, y, q, header, onPick: async (i) => {
        if (busy) return;
        busy = true;
        try {
          const res = await gameState.drivingQuizAnswer(q.id, i);
          audioManager.playSfx('click');
          if (res.quiz_result) { finish(res.quiz_result); return; }
          idx += 1;
          render();
        } catch (e) {
          // Session gone (expired / aborted elsewhere) — close instead of stranding the child.
          showToast(scene, messageFor(e));
          finish(null);
        } finally { busy = false; }
      } });
      items.push(...r.items);
    };

    closeBtn.on('pointerup', async () => {
      if (busy) return;
      if (kind === 'exam') {
        busy = true;
        // Nested confirm on top of this modal: temporarily hide our layer so it reads clearly.
        // inputLock is a COUNTER and showModal locks/unlocks around itself, so our own lock
        // still stands while it is open — no re-lock afterwards (that would leak a lock).
        layer.setVisible(false);
        const ok = await showModal(scene, {
          title: 'Przerwać egzamin?',
          body: 'Przerwany egzamin liczy się jako oblany. Następne podejście jutro.',
          confirmLabel: 'Przerwij', cancelLabel: 'Wracam',
        });
        layer.setVisible(true);
        if (!ok) { busy = false; return; }
      }
      // Stay busy across the abort POST (and never clear it): a second ✕ tap while it is
      // in flight would otherwise abort + finish twice.
      busy = true;
      try { await gameState.drivingQuizAbort(); } catch { /* already gone server-side */ }
      finish(null);
    });

    render();
  });
}

const BTN_H = 52, BTN_GAP = 10, BTN_STEP = BTN_H + BTN_GAP;

// Draws one question into `layer`: header line, optional illustration, question text and one
// button per option. With an image the block is bottom-anchored (buttons at the bottom, the
// picture fills the rest); without one the text+buttons block is centred so the card doesn't
// show a big empty gap. Returns the created objects and the buttons (for lesson feedback).
function renderQuestion({ scene, layer, x, y, q, header, onPick, reserveBottom = 0 }) {
  const items = [];
  const top = y - H / 2 + 20;
  const headerT = iconText(scene, x - W / 2 + 24, top, header, { fontFamily: 'system-ui', fontSize: '18px', color: '#666' }).setOrigin(0, 0);
  items.push(headerT);

  const hasImage = !!(q.image && scene.textures.exists(q.image));
  const text = iconText(scene, x, 0, q.text, { fontFamily: 'system-ui', fontSize: '24px', color: '#111', align: 'center', wordWrap: { width: W - 80 } }).setOrigin(0.5, 0);
  const blockH = text.height + 16 + q.options.length * BTN_STEP;
  // `reserveBottom` keeps room under the buttons (lesson mode's „Dalej ▶”).
  const btnTop = hasImage
    ? y + H / 2 - 24 - reserveBottom - q.options.length * BTN_STEP
    : y - (blockH + reserveBottom) / 2 + text.height + 16 + 30;   // centred block (30 = feedback line)
  text.y = btnTop - 16 - text.height;
  items.push(text);

  if (hasImage) {
    const areaTop = top + 34, areaBottom = text.y - 12;
    const availH = Math.max(60, areaBottom - areaTop);
    const tex = scene.textures.get(q.image).getSourceImage();
    const sc = Math.min(IMG_W / tex.width, IMG_H / tex.height, availH / tex.height);
    const img = scene.add.image(x, (areaTop + areaBottom) / 2, q.image);
    img.setDisplaySize(tex.width * sc, tex.height * sc);
    items.push(img);
  }

  const buttons = q.options.map((opt, i) => {
    const by = btnTop + BTN_H / 2 + i * BTN_STEP;
    const btn = scene.add.rectangle(x, by, W - 120, BTN_H, 0xffd166).setStrokeStyle(2, 0x333333).setInteractive({ useHandCursor: true });
    const txt = iconText(scene, x, by, opt, { fontFamily: 'system-ui', fontSize: '20px', color: '#000', wordWrap: { width: W - 160 }, align: 'center' }).setOrigin(0.5);
    items.push(btn, txt);
    btn.on('pointerup', () => onPick(i));
    return btn;
  });
  layer.add(items);
  const bottom = btnTop + q.options.length * BTN_STEP;
  return { items, buttons, textY: text.y, bottom };
}

// Lesson mode: the topic blurb first, then every question of the topic with INSTANT
// feedback — the picked button turns green/red, the correct one green, the explanation
// appears, and „Dalej ▶” moves on. Nothing is posted to the server; `correctIdx` is known.
export async function showLessonQuiz(scene, { title, blurb, questions }) {
  await loadPictures(scene, questions);
  return new Promise((resolve) => {
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);
    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff).setStrokeStyle(4, 0x333333);
    const closeBtn = iconText(scene, x + W / 2 - 14, y - H / 2 + 8, '×', { fontFamily: 'system-ui', fontSize: '26px', color: '#a33', fontStyle: 'bold' })
      .setOrigin(1, 0).setInteractive({ useHandCursor: true });
    layer.add([overlay, box, closeBtn]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });
    let items = [];
    const clear = () => { items.forEach((o) => o.destroy()); items = []; };
    let closed = false;
    const finish = () => { if (closed) return; closed = true; audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(); };
    closeBtn.on('pointerup', finish);

    const nextButton = (label, by, fn) => {
      const btn = scene.add.rectangle(x, by, 260, 48, 0x6fcf97).setStrokeStyle(2, 0x2e7d4f).setInteractive({ useHandCursor: true });
      const txt = iconText(scene, x, by, label, { fontFamily: 'system-ui', fontSize: '20px', color: '#000', fontStyle: 'bold' }).setOrigin(0.5);
      btn.on('pointerup', fn);
      items.push(btn, txt);
      layer.add([btn, txt]);
    };

    let idx = -1;   // -1 = the blurb slide
    const render = () => {
      clear();
      if (idx < 0) {
        const t = iconText(scene, x, y - H / 2 + 24, `{book} ${title}`, { fontFamily: 'system-ui', fontSize: '26px', color: '#111', fontStyle: 'bold' }).setOrigin(0.5, 0);
        const b = iconText(scene, x, y - 40, blurb, { fontFamily: 'system-ui', fontSize: '22px', color: '#222', align: 'center', wordWrap: { width: W - 120 }, lineSpacing: 6 }).setOrigin(0.5);
        items.push(t, b);
        layer.add([t, b]);
        nextButton(`Zaczynamy {arrow_right} (${questions.length} pytań)`, b.y + b.height / 2 + 60, () => { idx = 0; render(); });
        return;
      }
      const q = questions[idx];
      let answered = false;
      const r = renderQuestion({ scene, layer, x, y, q, header: `${title} · pytanie ${idx + 1} / ${questions.length}`, reserveBottom: 64, onPick: (i) => {
        if (answered) return;
        answered = true;
        const ok = i === q.correctIdx;
        audioManager.playSfx('click');
        r.buttons.forEach((btn, j) => {
          btn.disableInteractive();
          if (j === q.correctIdx) btn.setFillStyle(0x8fe0a5);
          else if (j === i) btn.setFillStyle(0xf28b82);
          else btn.setFillStyle(0xeeeeee);
        });
        const fb = iconText(scene, x, r.textY - 12, `${ok ? '{check} Dobrze!' : '{cross} Nie tym razem.'} {bulb} ${q.explanation}`,
          { fontFamily: 'system-ui', fontSize: '18px', color: ok ? '#1b5e20' : '#8b1a1a', align: 'center', wordWrap: { width: W - 100 }, backgroundColor: '#ffffffee', padding: { x: 8, y: 4 } }).setOrigin(0.5, 1);
        items.push(fb);
        layer.add(fb);
        const last = idx === questions.length - 1;
        nextButton(last ? 'Koniec lekcji {check}' : 'Dalej {arrow_right}', r.bottom + 36, () => { if (last) finish(); else { idx += 1; render(); } });
      } });
      items.push(...r.items);
    };
    render();
  });
}

export function showQuizResultModal(scene, result) {
  const lines = result.wrong.map((w, i) =>
    `${i + 1}. ${w.text}\n   Twoja odpowiedź: ${w.your}\n   Poprawna: ${w.correct}\n   ${w.explanation}`);
  const body = lines.length ? lines.join('\n\n') : 'Wszystkie odpowiedzi dobre! {star_full}';
  return showScrollableTextModal(scene, { title: resultHeadline(result), body });
}

// Simple long-text modal with ✕ (used by results and lessons). Long bodies page with ◀ ▶:
// paragraphs are packed into a page until the REAL rendered height (word wrap included)
// would run into the pager row — measuring beats a character budget, which under- or
// overfills depending on how the text wraps.
const BODY_TOP = 70, BODY_MAX_H = H - BODY_TOP - 66;   // 66 = pager row + margin

export function showScrollableTextModal(scene, { title, body }) {
  return new Promise((resolve) => {
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2001);
    lockInput(scene);
    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xfdf6e3).setStrokeStyle(4, 0x5b4636);
    const titleT = iconText(scene, x - W / 2 + 28, y - H / 2 + 20, title, { fontFamily: 'system-ui', fontSize: '26px', color: '#2e2116', fontStyle: 'bold' }).setOrigin(0, 0);
    const bodyT = iconText(scene, x - W / 2 + 28, y - H / 2 + BODY_TOP, '', { fontFamily: 'system-ui', fontSize: '18px', color: '#3b2d20', lineSpacing: 6, wordWrap: { width: W - 56 } }).setOrigin(0, 0);
    const closeBtn = iconText(scene, x + W / 2 - 12, y - H / 2 + 8, '×', { fontFamily: 'system-ui', fontSize: '26px', color: '#a33', fontStyle: 'bold' }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
    const prev = iconText(scene, x - 60, y + H / 2 - 36, '{arrow_left}', { fontFamily: 'system-ui', fontSize: '26px', color: '#2e2116' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    const next = iconText(scene, x + 60, y + H / 2 - 36, '{arrow_right}', { fontFamily: 'system-ui', fontSize: '26px', color: '#2e2116' }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    const pageT = iconText(scene, x, y + H / 2 - 36, '', { fontFamily: 'system-ui', fontSize: '16px', color: '#7a6a55' }).setOrigin(0.5);
    layer.add([overlay, box, titleT, bodyT, closeBtn, prev, next, pageT]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    const pages = [];
    let cur = '';
    for (const para of body.split('\n\n')) {
      const candidate = cur ? `${cur}\n\n${para}` : para;
      bodyT.setText(candidate);
      if (cur && bodyT.height > BODY_MAX_H) { pages.push(cur); cur = para; }
      else cur = candidate;
    }
    if (cur) pages.push(cur);
    let p = 0;
    const render = () => {
      bodyT.setText(pages[p] ?? '');
      pageT.setText(pages.length > 1 ? `${p + 1} / ${pages.length}` : '');
      prev.setVisible(p > 0); next.setVisible(p < pages.length - 1);
    };
    prev.on('pointerup', () => { p = Math.max(0, p - 1); render(); });
    next.on('pointerup', () => { p = Math.min(pages.length - 1, p + 1); render(); });
    const close = () => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(); };
    closeBtn.on('pointerup', close);
    overlay.on('pointerup', close);
    render();
  });
}
